if (typeof ProductImageZoom !== 'function') {
	class ProductImageZoom extends HTMLElement {
		constructor() {
			super();
			this.scale = 1;
			this.position = { x: 0, y: 0 };
			this.isDragging = false;
			this.lastTouchDistance = 0;
			this.init();
		}

		init() {
			if (!document.getElementById('zoom-modal')) {
				this.modal = document.createElement('div');
				this.modal.id = 'zoom-modal';
				this.modal.innerHTML = `
					<div class="zoom-modal__content">
						<button class="zoom-modal__close">×</button>
						<img class="zoom-modal__image" />
					</div>
				`;
				document.body.appendChild(this.modal);
			} else {
				this.modal = document.getElementById('zoom-modal');
			}

			this.image = this.modal.querySelector('.zoom-modal__image');
			this.setupEventListeners();
		}

		setupEventListeners() {
			// Open zoom
			this.addEventListener('click', () => {
				const originalImage = this.getAttribute('data-image');
				const highResImage = originalImage.replace(/_(small|medium|large|grande|compact|master)\./, '_2048x.');
				
				this.image.src = highResImage;
				this.modal.classList.add('opened');
				document.body.style.overflow = 'hidden';
				this.scale = 1;
				this.position = { x: 0, y: 0 };
				this.updateTransform();
			});

			// Close zoom
			this.modal.querySelector('.zoom-modal__close').addEventListener('click', () => {
				this.closeZoom();
			});

			this.modal.addEventListener('click', (e) => {
				if (e.target === this.modal) {
					this.closeZoom();
				}
			});

			// Mouse wheel zoom
			this.image.addEventListener('wheel', (e) => {
				e.preventDefault();
				const rect = this.image.getBoundingClientRect();
				const mouseX = e.clientX - rect.left;
				const mouseY = e.clientY - rect.top;

				const delta = -e.deltaY * 0.01;
				const newScale = Math.max(1, Math.min(4, this.scale * (1 + delta)));
				const scaleDiff = newScale - this.scale;

				if (scaleDiff !== 0) {
					const scaleRatio = newScale / this.scale;
					this.position.x -= (mouseX * scaleDiff) / this.scale;
					this.position.y -= (mouseY * scaleDiff) / this.scale;
					this.scale = newScale;
					this.constrainPosition();
					this.updateTransform();
				}
			});

			// Mouse drag
			this.image.addEventListener('mousedown', (e) => {
				if (this.scale > 1) {
					e.preventDefault();
					this.isDragging = true;
					this.startX = e.clientX - this.position.x;
					this.startY = e.clientY - this.position.y;
				}
			});

			window.addEventListener('mousemove', (e) => {
				if (this.isDragging) {
					e.preventDefault();
					this.position.x = e.clientX - this.startX;
					this.position.y = e.clientY - this.startY;
					this.constrainPosition();
					this.updateTransform();
				}
			});

			window.addEventListener('mouseup', () => {
				this.isDragging = false;
			});

			// Touch events
			this.image.addEventListener('touchstart', (e) => {
				if (e.touches.length === 2) {
					e.preventDefault();
					const touch1 = e.touches[0];
					const touch2 = e.touches[1];
					this.lastTouchDistance = Math.hypot(
						touch2.clientX - touch1.clientX,
						touch2.clientY - touch1.clientY
					);
				} else if (e.touches.length === 1 && this.scale > 1) {
					const touch = e.touches[0];
					this.isDragging = true;
					this.startX = touch.clientX - this.position.x;
					this.startY = touch.clientY - this.position.y;
				}
			}, { passive: false });

			this.image.addEventListener('touchmove', (e) => {
				if (e.touches.length === 2) {
					e.preventDefault();
					const touch1 = e.touches[0];
					const touch2 = e.touches[1];
					const distance = Math.hypot(
						touch2.clientX - touch1.clientX,
						touch2.clientY - touch1.clientY
					);

					const delta = (distance - this.lastTouchDistance) * 0.01;
					const newScale = Math.max(1, Math.min(4, this.scale * (1 + delta)));

					if (newScale !== this.scale) {
						this.scale = newScale;
						this.constrainPosition();
						this.updateTransform();
					}

					this.lastTouchDistance = distance;
				} else if (e.touches.length === 1 && this.isDragging) {
					e.preventDefault();
					const touch = e.touches[0];
					this.position.x = touch.clientX - this.startX;
					this.position.y = touch.clientY - this.startY;
					this.constrainPosition();
					this.updateTransform();
				}
			}, { passive: false });

			this.image.addEventListener('touchend', () => {
				this.isDragging = false;
				this.lastTouchDistance = 0;
			});
		}

		constrainPosition() {
			if (this.scale <= 1) {
				this.position = { x: 0, y: 0 };
				return;
			}

			const rect = this.image.getBoundingClientRect();
			const maxX = (rect.width * (this.scale - 1)) / 2;
			const maxY = (rect.height * (this.scale - 1)) / 2;

			this.position.x = Math.min(maxX, Math.max(-maxX, this.position.x));
			this.position.y = Math.min(maxY, Math.max(-maxY, this.position.y));
		}

		updateTransform() {
			this.image.style.transform = `translate(${this.position.x}px, ${this.position.y}px) scale(${this.scale})`;
		}

		closeZoom() {
			this.modal.classList.remove('opened');
			document.body.style.overflow = '';
			setTimeout(() => {
				this.image.src = '';
				this.scale = 1;
				this.position = { x: 0, y: 0 };
				this.updateTransform();
			}, 300);
		}
	}

	if (typeof customElements.get('product-image-zoom') === 'undefined') {
		customElements.define('product-image-zoom', ProductImageZoom);
	}
}

// Add required CSS
const style = document.createElement('style');
style.textContent = `
	#zoom-modal {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.9);
		z-index: 999;
		opacity: 0;
		visibility: hidden;
		transition: opacity 0.3s ease;
		display: flex;
		align-items: center;
		justify-content: center;
		touch-action: none;
	}

	#zoom-modal.opened {
		opacity: 1;
		visibility: visible;
	}

	.zoom-modal__content {
		position: relative;
		max-width: 90%;
		max-height: 90%;
		margin: auto;
		overflow: hidden;
	}

	.zoom-modal__image {
		display: block;
		max-width: 100%;
		max-height: 90vh;
		height: auto;
		margin: auto;
		transition: transform 0.2s ease;
		transform-origin: center;
		touch-action: none;
		-webkit-user-select: none;
		user-select: none;
	}

	.zoom-modal__close {
		position: absolute;

		width: 40px;
		height: 40px;
		background: rgba(255, 255, 255, 0.1);
		border: none;
		border-radius: 50%;
		color: white;
		font-size: 45px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
	}

	.zoom-modal__close:hover {
		background: rgba(255, 255, 255, 0.2);
	}
`;
document.head.appendChild(style);